import { IbanElement } from '@stripe/react-stripe-js';
import { useState } from 'react';

import Loader from 'hb-react/shared/components/Loader';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import StripeError from '../../errors/StripeError';
import { displayAmount, displayCurrency } from '../../utils';
import MandateText from '../MandateText';

const IBAN_STYLE = {
  base: {
    color: '#32325d',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4',
    },
    ':-webkit-autofill': {
      color: '#32325d',
    },
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a',
    ':-webkit-autofill': {
      color: '#fa755a',
    },
  },
};

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  style: IBAN_STYLE,
};

const IBANForm = ({
  stripeAccountId,
  userName,
  userEmail,
  amount,
  currency,
  endpointUrl,
  endpointData,
  onPaymentSuccess,
  stripe,
  elements,
  payment_type: paymentType,
  payment_type_id: paymentTypeId,
  paymentAccountConfig,
}) => {
  const [loading, setLoading] = useState(false);

  const handleError = (error) => {
    displayNotif(
      'error',
      s__('StripeSepaDebit|An error occurred processing the payment'),
    );
    setLoading(false);
    errorReporting.captureException(error);
  };

  const initializePayment = (setupIntent) => {
    const stripePayload = {
      transaction_token: setupIntent.id,
      gateway: 'Stripe',
      payment_method: 'sepa_debit',
      async_payment: true,
    };

    return endpointData instanceof Function
      ? endpointData(stripePayload)
      : {
          ...endpointData,
          ...stripePayload,
        };
  };

  const executePayment = (setupIntent) => {
    axios
      .post(endpointUrl, initializePayment(setupIntent))
      .then(({ data }) => {
        if (onPaymentSuccess) {
          onPaymentSuccess(data);
        }
      })
      .catch((error) => {
        handleError(
          new StripeError(
            `Error executing payment for ${paymentType} ${paymentTypeId}`,
            error,
          ),
        );
      });
  };

  const confirmSepaDebitSetup = (data) => {
    const email = userEmail;
    const name = userName;
    const iban = elements.getElement(IbanElement);

    stripe
      .confirmSepaDebitSetup(data.client_secret, {
        payment_method: {
          sepa_debit: iban,
          billing_details: { name, email },
        },
      })
      .then(({ setupIntent, error }) => {
        if (error) {
          handleError(
            new StripeError('Error confirming sepa debit setup intent', error),
          );
        } else {
          executePayment(setupIntent);
        }
      });
  };

  const createSepaDebitSetupIntent = () => {
    axios
      .post(Routes.stripe_setup_intent_path(), {
        external_account_id: stripeAccountId,
        payment_method_types: ['sepa_debit'],
      })
      .then(({ data }) => {
        confirmSepaDebitSetup(data);
      })
      .catch((error) => {
        handleError(new StripeError('Error creating setup intent', error));
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    createSepaDebitSetupIntent();
  };

  return (
    <form onSubmit={handleSubmit}>
      {loading && <Loader fillParent />}
      <div className="push" data-testid="stripe-sepa-debit-element">
        <label className="push" data-testid="stripe-iban">
          {__('IBAN')}
          <IbanElement
            className="push--top push--left push--right"
            options={IBAN_ELEMENT_OPTIONS}
          />
        </label>

        <MandateText
          merchantDisplayName={paymentAccountConfig.merchant_display_name}
        />

        <div className="text--center push--top">
          <button
            data-testid="stripe-sepa-debit-form-submit"
            className="button button--primary button__validation--blue"
            type="submit"
          >
            {__('Pay %{currency} %{amount}').i__({
              currency: displayCurrency(currency),
              amount: displayAmount(amount),
            })}
          </button>
        </div>
      </div>
    </form>
  );
};

export default IBANForm;
